import { initializeApp, getApps, FirebaseApp } from "@firebase/app";
import { getAuth, Auth } from "@firebase/auth";
import { getStorage, FirebaseStorage } from "@firebase/storage";

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "disco-outpost-268223",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // Required for Firebase Analytics (GA4). Analytics stays disabled when unset
  // (see integrations/firebase/analytics.ts).
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase app (singleton pattern)
// Wrap everything in try-catch to prevent module load failures
let app: FirebaseApp | null = null;
let firebaseAuthInstance: Auth | null = null;
let firebaseStorageInstance: FirebaseStorage | null = null;
let firebaseInitialized = false;

try {
  // Only initialize in browser environment
  if (typeof window !== 'undefined') {
    if (getApps().length === 0) {
      // Only initialize if we have the required config
      if (firebaseConfig.apiKey && firebaseConfig.projectId) {
        app = initializeApp(firebaseConfig);
        firebaseAuthInstance = getAuth(app);
        firebaseStorageInstance = getStorage(app);
        firebaseInitialized = true;
      } else {
        console.warn("🔥 Firebase config incomplete, Firebase Auth will not be available");
      }
    } else {
      app = getApps()[0];
      firebaseAuthInstance = getAuth(app);
      firebaseStorageInstance = getStorage(app);
      firebaseInitialized = true;
    }
  }
} catch (error) {
  console.error("🔥 Error initializing Firebase:", error);
  // Don't throw - allow module to load even if Firebase fails
}

// Helper to check if Firebase is properly initialized
export const isFirebaseInitialized = () => firebaseInitialized && firebaseAuthInstance !== null;

// Safe fallback Auth object that implements the Auth interface
const createSafeFallbackAuth = (): Auth => ({
  currentUser: null,
  app: {} as any,
  settings: {} as any,
  onAuthStateChanged: () => () => {},
  signOut: async () => {},
  useDeviceLanguage: () => {},
  languageCode: null,
  tenantId: null,
  name: '[DEFAULT]',
} as unknown as Auth);

// Export Firebase Auth instance
// Wrap entire export in try-catch to prevent module load failures
export const firebaseAuth: Auth = (() => {
  try {
    if (firebaseAuthInstance && firebaseInitialized) {
      // Configure Firebase Auth for localhost development
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        try {
          firebaseAuthInstance.settings.appVerificationDisabledForTesting = false;
        } catch (e) {
          // Ignore settings errors
        }
      }
      return firebaseAuthInstance;
    }
    
    // If Firebase isn't initialized, return safe fallback
    // Don't try to create dummy apps as they require valid config
    return createSafeFallbackAuth();
  } catch (error) {
    // If everything fails, return a completely safe fallback object
    console.error("🔥 Critical: Firebase Auth export failed, using safe fallback:", error);
    return createSafeFallbackAuth();
  }
})();

// Export Firebase Storage instance
export const firebaseStorage: FirebaseStorage = (() => {
  try {
    if (firebaseStorageInstance && firebaseInitialized) {
      return firebaseStorageInstance;
    }
    // Fallback: attempt to get storage from the app if available
    if (app) {
      return getStorage(app);
    }
    throw new Error("Firebase Storage not initialized");
  } catch (error) {
    console.error("🔥 Critical: Firebase Storage export failed:", error);
    throw error;
  }
})();

// Export Firebase app instance
export { app };

// Helper function to get Firebase ID token
export const getFirebaseIdToken = async (): Promise<string | null> => {
  try {
    // Ensure we're in a browser environment
    if (typeof window === 'undefined') {
      return null;
    }
    
    // Check if Firebase Auth is initialized
    if (!firebaseAuthInstance || !app) {
      return null;
    }
    
    const user = firebaseAuthInstance.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken(false);
  } catch (error) {
    // Silently fail - Firebase might not be ready yet
    return null;
  }
};

// Helper function to get Firebase ID token with force refresh
export const getFirebaseIdTokenWithRefresh = async (): Promise<string | null> => {
  try {
    // Ensure we're in a browser environment
    if (typeof window === 'undefined') {
      return null;
    }
    
    // Check if Firebase Auth is initialized
    if (!firebaseAuthInstance || !app) {
      return null;
    }
    
    const user = firebaseAuthInstance.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken(true);
  } catch (error) {
    console.error("Error getting Firebase ID token with refresh:", error);
    return null;
  }
};
